import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useOrg } from '../lib/org';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import {
  Spinner,
  ErrorAlert,
  EmptyState,
  Field,
  TextareaField,
  SelectField,
} from '../components/ui';
import VendorLogo from '../components/VendorLogo';
import ListingPill from '../components/ListingPill';
import VendorPipeline from '../components/VendorPipeline';
import VendorBilling from '../components/VendorBilling';
import VendorLogoUpload from '../components/VendorLogoUpload';
import {
  LIMITS,
  PRICE_TYPES,
  PRICE_UNITS,
  PRICE_UNIT_LABELS,
  REVIEW_POLICY_LABELS,
  amountToCents,
  centsToInput,
  feeLabel,
  listingReview,
  servicesFromCsv,
  servicesToCsv,
} from '../lib/marketplace';
import { fieldErrorsFrom, focusFirstInvalid } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

function ProfileForm({ vendor, isNew, activeOrg, onSaved, onCancel }) {
  const toast = useToast();
  const formRef = useRef(null);
  const [form, setForm] = useState(() => ({
    scope: vendor?.scope || 'platform',
    businessName: vendor?.businessName || '',
    description: vendor?.description || '',
    logoUrl: vendor?.logoUrl || '',
    email: vendor?.contact?.email || '',
    phone: vendor?.contact?.phone || '',
    website: vendor?.contact?.website || '',
    serviceArea: vendor?.contact?.serviceArea || '',
    discoverable: Boolean(vendor?.discoverable),
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined, [`contact.${key}`]: undefined }));
  };

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const body = {
      businessName: form.businessName,
      description: form.description,
      logoUrl: form.logoUrl.trim() || null,
      contact: {
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        serviceArea: form.serviceArea.trim(),
      },
      discoverable: form.scope === 'platform' && form.discoverable,
      ...(isNew ? { scope: form.scope } : {}),
    };
    try {
      const data = isNew
        ? await marketApi.createVendor(body)
        : await marketApi.updateVendor(vendor.vendorId, body);
      toast.success(`${data.vendor.businessName} saved`);
      onSaved(data.vendor);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      focusFirstInvalid(formRef.current);
      toast.error(err.message || 'The profile was not saved');
    } finally {
      setSaving(false);
    }
  }

  const err = key => errors[key] || errors[`contact.${key}`];
  return (
    <form ref={formRef} onSubmit={save} className={cn(ui.card, 'mb-6')} noValidate>
      <h2 className="mt-0 text-title-m">{isNew ? 'New vendor profile' : 'Profile'}</h2>
      {isNew && (
        <fieldset className="mb-4">
          <legend className={ui.label}>Where you will use it</legend>
          <label className={cn(ui.row, 'text-body-s')}>
            <input
              type="radio"
              name="scope"
              checked={form.scope === 'platform'}
              onChange={() => set('scope', 'platform')}
            />
            A platform profile you can list in any organization you join
          </label>
          <label className={cn(ui.row, 'text-body-s')}>
            <input
              type="radio"
              name="scope"
              checked={form.scope === 'org'}
              disabled={!activeOrg}
              onChange={() => set('scope', 'org')}
            />
            Only in {activeOrg ? activeOrg.name : 'the organization you choose'}
          </label>
          {err('scope') && <p className={ui.fieldError}>{err('scope')}</p>}
        </fieldset>
      )}
      <Field
        label="Business name"
        value={form.businessName}
        onChange={e => set('businessName', e.target.value)}
        maxLength={LIMITS.businessNameMax}
        error={err('businessName')}
      />
      <TextareaField
        label="About your business"
        value={form.description}
        onChange={e => set('description', e.target.value)}
        rows={5}
        maxLength={LIMITS.vendorDescriptionMax}
        error={err('description')}
      />
      <Field
        label="Logo address"
        hint="An https:// link to a square image, such as one on your website. An uploaded logo, once scanned, is shown instead."
        value={form.logoUrl}
        onChange={e => set('logoUrl', e.target.value)}
        inputMode="url"
        error={err('logoUrl')}
      />
      <div className="grid gap-x-4 md:grid-cols-2">
        <Field
          label="Contact email"
          type="email"
          value={form.email}
          onChange={e => set('email', e.target.value)}
          error={err('email')}
        />
        <Field
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={e => set('phone', e.target.value)}
          error={err('phone')}
        />
        <Field
          label="Website"
          inputMode="url"
          placeholder="https://"
          value={form.website}
          onChange={e => set('website', e.target.value)}
          error={err('website')}
        />
        <Field
          label="Service area"
          hint="For example: Los Angeles County, or nationwide"
          value={form.serviceArea}
          onChange={e => set('serviceArea', e.target.value)}
          maxLength={LIMITS.serviceAreaMax}
          error={err('serviceArea')}
        />
      </div>
      {form.scope === 'platform' && (
        <label className="mb-4 flex items-center gap-2 text-body-s">
          <input
            type="checkbox"
            className={ui.checkbox}
            checked={form.discoverable}
            onChange={e => set('discoverable', e.target.checked)}
          />
          Let organization admins find this profile and invite me to their marketplace
        </label>
      )}
      <div className={ui.row}>
        <button className={ui.btnPrimary} type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save profile'}
        </button>
        {onCancel && (
          <button className={ui.btnGhost} type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function emptyService() {
  return { name: '', description: '', priceType: 'fixed', price: '', unit: 'one-time' };
}

function toRow(s) {
  return {
    name: s.name,
    description: s.description || '',
    priceType: s.priceType,
    price: s.priceType === 'quote' ? '' : centsToInput(s.priceCents),
    unit: s.unit,
  };
}

function ServicesEditor({ vendor, onSaved }) {
  const toast = useToast();
  const [rows, setRows] = useState(() => (vendor.services || []).map(toRow));
  const [errors, setErrors] = useState({});
  const [csvErrors, setCsvErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const update = (i, key, value) => {
    setRows(prev => prev.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
    setErrors(prev => ({ ...prev, [`services.${i}`]: undefined }));
  };

  async function onCsv(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 200_000) {
      setCsvErrors([{ row: 1, message: 'The file is larger than 200 KB' }]);
      return;
    }
    const result = servicesFromCsv(await file.text());
    setCsvErrors(result.errors);
    if (!result.errors.length) {
      setRows(result.services.map(toRow));
      toast.success(
        `${result.services.length} services read from ${file.name}. Review them, then save.`
      );
    }
  }

  function download() {
    const blob = new Blob([servicesToCsv(vendor.services || [])], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'services.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function save() {
    const found = {};
    const services = rows.map((r, i) => {
      const priceCents = r.priceType === 'quote' ? null : amountToCents(r.price);
      if (r.priceType !== 'quote' && Number.isNaN(priceCents)) {
        found[`services.${i}`] = 'Price: enter an amount such as 150 or 150.00';
      }
      return {
        name: r.name,
        description: r.description,
        priceType: r.priceType,
        priceCents,
        unit: r.unit,
      };
    });
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setSaving(true);
    try {
      const data = await marketApi.updateVendor(vendor.vendorId, { services });
      toast.success('Services saved');
      setRows(data.vendor.services.map(toRow));
      setErrors({});
      onSaved(data.vendor);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'The services were not saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={cn(ui.card, 'mb-6')} aria-labelledby="services-editor-heading">
      <h2 id="services-editor-heading" className="mt-0 text-title-m">
        Services and pricing
      </h2>
      <p className={cn(ui.hint, 'mt-0 mb-3')}>
        Up to {LIMITS.servicesMax} services. To import a list, upload a CSV with the columns name,
        description, price_type (fixed, from or quote), price and unit (one-time, hour, month, year
        or project).
      </p>
      <div className={cn(ui.row, 'mb-4')}>
        <label className={cn(ui.btnSecondary, ui.btnSm)}>
          Import a CSV
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={onCsv}
          />
        </label>
        <button className={cn(ui.btnGhost, ui.btnSm)} type="button" onClick={download}>
          Download as CSV
        </button>
      </div>
      {csvErrors.length > 0 && (
        <div className={ui.alertError} role="alert">
          <p className="mt-0 font-semibold">The file was not imported:</p>
          <ul className="mb-0">
            {csvErrors.map(e => (
              <li key={`${e.row}-${e.message}`}>
                Row {e.row}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rows.length === 0 && <p className={ui.muted}>No services yet.</p>}
      {rows.map((r, i) => (
        <fieldset key={i} className={cn(ui.cardFlat, 'mb-3 p-4')}>
          <legend className="sr-only">Service {i + 1}</legend>
          <div className="grid gap-x-4 md:grid-cols-[2fr_1fr_1fr_1fr]">
            <Field
              label="Service"
              value={r.name}
              onChange={e => update(i, 'name', e.target.value)}
              maxLength={LIMITS.serviceNameMax}
            />
            <SelectField
              label="Price"
              value={r.priceType}
              onChange={e => update(i, 'priceType', e.target.value)}
            >
              {PRICE_TYPES.map(t => (
                <option key={t} value={t}>
                  {t === 'fixed' ? 'Fixed' : t === 'from' ? 'From' : 'Quote'}
                </option>
              ))}
            </SelectField>
            <Field
              label="Amount (USD)"
              inputMode="decimal"
              value={r.price}
              disabled={r.priceType === 'quote'}
              onChange={e => update(i, 'price', e.target.value)}
            />
            <SelectField
              label="Unit"
              value={r.unit}
              onChange={e => update(i, 'unit', e.target.value)}
            >
              {PRICE_UNITS.map(u => (
                <option key={u} value={u}>
                  {PRICE_UNIT_LABELS[u]}
                </option>
              ))}
            </SelectField>
          </div>
          <Field
            label="Description"
            value={r.description}
            onChange={e => update(i, 'description', e.target.value)}
            maxLength={LIMITS.serviceDescriptionMax}
            error={errors[`services.${i}`]}
          />
          <button
            type="button"
            className={cn(ui.btnGhost, ui.btnSm)}
            onClick={() => setRows(prev => prev.filter((_, j) => j !== i))}
          >
            Remove {r.name || `service ${i + 1}`}
          </button>
        </fieldset>
      ))}
      <div className={ui.row}>
        <button
          type="button"
          className={cn(ui.btnSecondary, ui.btnSm)}
          disabled={rows.length >= LIMITS.servicesMax}
          onClick={() => setRows(prev => [...prev, emptyService()])}
        >
          Add a service
        </button>
        <button type="button" className={ui.btnPrimary} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save services'}
        </button>
      </div>
    </section>
  );
}

function ApplyForm({ vendor, activeOrg, invited, onApplied, onCancel }) {
  const toast = useToast();
  const formRef = useRef(null);
  const [categories, setCategories] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [answers, setAnswers] = useState({});
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    marketApi
      .listCategories()
      .then(d => setCategories(d.categories))
      .catch(setError);
  }, [activeOrg.orgId]);

  if (error) return <ErrorAlert error={error} />;
  if (!categories) return <Spinner label="Loading categories..." />;

  const flat = categories.flatMap(c => [c, ...c.children]);
  const chosen = flat.filter(c => picked.has(c.categoryId));
  const review = listingReview(chosen);

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      const data = await marketApi.applyToList(vendor.vendorId, [...picked], answers);
      toast.success(`Application sent to ${activeOrg.name}`);
      onApplied(data.listing);
    } catch (err) {
      const fields = fieldErrorsFrom(err);
      setErrors(fields);
      focusFirstInvalid(formRef.current);
      toast.error(err.message || 'The application was not sent');
    } finally {
      setSending(false);
    }
  }

  return (
    <form ref={formRef} onSubmit={submit} className={cn(ui.cardFlat, 'mt-3')} noValidate>
      <h3 className="mt-0 text-title-s">
        {invited ? 'Accept the invitation from' : 'Apply to'} {activeOrg.name}
      </h3>
      {flat.length === 0 ? (
        <p className={ui.muted}>{activeOrg.name} has no marketplace categories yet.</p>
      ) : (
        <fieldset className="mb-4">
          <legend className={ui.label}>
            Categories you serve (up to {LIMITS.categoriesPerListing})
          </legend>
          <div
            className={cn(ui.tableWrap, 'mt-2')}
            tabIndex={0}
            role="region"
            aria-label="Categories and their fees"
          >
            <table className={ui.table}>
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Lead fee on a won deal</th>
                  <th scope="col">Of which the organization fee</th>
                  <th scope="col">Review</th>
                </tr>
              </thead>
              <tbody>
                {flat.map(c => (
                  <tr key={c.categoryId}>
                    <td>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className={ui.checkbox}
                          checked={picked.has(c.categoryId)}
                          onChange={e =>
                            setPicked(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(c.categoryId);
                              else next.delete(c.categoryId);
                              return next;
                            })
                          }
                        />
                        {c.parentId ? `- ${c.name}` : c.name}
                      </label>
                    </td>
                    <td>{feeLabel(c.leadFee, 'the deal value')}</td>
                    <td>{feeLabel(c.orgFee, 'the lead fee')}</td>
                    <td>{REVIEW_POLICY_LABELS[c.reviewPolicy || 'standard'].label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {errors.categoryIds && <p className={ui.fieldError}>{errors.categoryIds}</p>}
        </fieldset>
      )}
      {review.requirements.length > 0 && (
        <fieldset className="mb-4">
          <legend className={ui.label}>What these categories require</legend>
          <p className={cn(ui.hint, 'mt-0')}>
            The organization validates each one before approving you. Give the number, issuing state
            or agency, and expiry where they apply.
          </p>
          {review.requirements.map(r => (
            <Field
              key={r.key}
              label={`${r.label} (${r.categoryName})`}
              value={answers[r.key] || ''}
              onChange={e => setAnswers(prev => ({ ...prev, [r.key]: e.target.value }))}
              maxLength={LIMITS.requirementValueMax}
              error={errors[`requirements.${r.key}`]}
            />
          ))}
        </fieldset>
      )}
      <label className="mb-1 flex items-start gap-2 text-body-s">
        <input
          type="checkbox"
          className={cn(ui.checkbox, 'mt-1')}
          checked={terms}
          onChange={e => setTerms(e.target.checked)}
          aria-invalid={errors.acceptTerms ? 'true' : undefined}
        />
        <span>
          I accept the <Link to="/marketplace/terms">marketplace terms</Link>, including the fees
          above on deals sourced through {activeOrg.name}, and that its admins decide on listings
          and reviews.
        </span>
      </label>
      {errors.acceptTerms && <p className={ui.fieldError}>{errors.acceptTerms}</p>}
      <div className={cn(ui.row, 'mt-4')}>
        <button
          className={ui.btnPrimary}
          type="submit"
          disabled={sending || !terms || picked.size === 0}
        >
          {sending ? 'Sending...' : invited ? 'Accept and apply' : 'Send application'}
        </button>
        <button className={ui.btnGhost} type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ListingsPanel({ vendor, activeOrg, orgs, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [applying, setApplying] = useState(false);
  const listings = vendor.listings || [];
  const here = activeOrg ? listings.find(l => l.orgId === activeOrg.orgId) : null;
  const canApplyHere =
    activeOrg &&
    (vendor.scope === 'platform' || vendor.scopeOrgId === activeOrg.orgId) &&
    (!here || ['invited', 'declined', 'withdrawn'].includes(here.status));
  const orgName = id => orgs.find(o => o.orgId === id)?.name || 'An organization you are not in';

  async function withdraw(listing) {
    const ok = await confirm({
      title: 'Withdraw this listing?',
      message: `${vendor.businessName} leaves ${orgName(listing.orgId)}'s marketplace. You can apply again later.`,
      confirmLabel: 'Withdraw',
      danger: true,
    });
    if (!ok) return;
    try {
      await marketApi.actOnListing(listing.listingId, 'withdraw');
      toast.success('Listing withdrawn');
      onChanged();
    } catch (err) {
      toast.error(err.message || 'The listing was not withdrawn');
    }
  }

  return (
    <section className={cn(ui.card, 'mb-6')} aria-labelledby="listings-heading">
      <h2 id="listings-heading" className="mt-0 text-title-m">
        Where you are listed
      </h2>
      {listings.length === 0 ? (
        <p className={ui.muted}>Not listed anywhere yet.</p>
      ) : (
        <ul className="m-0 mb-4 list-none p-0">
          {listings.map(l => (
            <li key={l.listingId} className={cn(ui.row, ui.spread, 'border-b border-z-line py-2')}>
              <span>
                <strong>{orgName(l.orgId)}</strong> <ListingPill status={l.status} />
                {l.statusNote && <span className={cn(ui.hint, 'ml-2')}>Note: {l.statusNote}</span>}
                {l.status === 'invited' && l.inviteNote && (
                  <span className={cn(ui.hint, 'ml-2')}>&ldquo;{l.inviteNote}&rdquo;</span>
                )}
              </span>
              {['invited', 'pending', 'approved', 'suspended'].includes(l.status) && (
                <button
                  type="button"
                  className={cn(ui.btnGhost, ui.btnSm)}
                  onClick={() => withdraw(l)}
                >
                  {l.status === 'invited' ? 'Decline invitation' : 'Withdraw'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!activeOrg ? (
        <p className={ui.hint}>
          Choose an organization you belong to, then apply to its marketplace.
        </p>
      ) : applying ? (
        <ApplyForm
          vendor={vendor}
          activeOrg={activeOrg}
          invited={here?.status === 'invited'}
          onApplied={() => {
            setApplying(false);
            onChanged();
          }}
          onCancel={() => setApplying(false)}
        />
      ) : canApplyHere ? (
        <button type="button" className={ui.btnSecondary} onClick={() => setApplying(true)}>
          {here?.status === 'invited'
            ? `Accept ${activeOrg.name}'s invitation`
            : `Apply to ${activeOrg.name}`}
        </button>
      ) : here ? null : (
        <p className={ui.hint}>This profile was made for another organization.</p>
      )}
    </section>
  );
}

/**
 * /vendor: the vendor workspace (docs/MarketplaceDesign.md section 6.5):
 * profiles (platform or organization, D68 item 9), the services list with
 * CSV import, and listings with applications and invitations.
 */
export default function VendorWorkspace() {
  const { activeOrg, orgs } = useOrg();
  const [vendors, setVendors] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await marketApi.listMyVendors();
      setVendors(data.vendors);
      setSelected(prev =>
        prev && data.vendors.some(v => v.vendorId === prev)
          ? prev
          : data.vendors[0]?.vendorId || null
      );
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const title = <h1 className={ui.h1}>Vendor workspace</h1>;
  if (error) {
    return (
      <>
        {title}
        <ErrorAlert error={error} />
      </>
    );
  }
  if (!vendors) {
    return (
      <>
        {title}
        <Spinner label="Loading your vendor profiles..." />
      </>
    );
  }
  const vendor = vendors.find(v => v.vendorId === selected);

  return (
    <>
      {title}
      <p className={cn(ui.muted, 'mb-4')}>
        Offer your services in the marketplaces of organizations you belong to. Each organization
        approves its own listings and sets its own fees.
      </p>
      <div className={cn(ui.row, 'mb-6')}>
        {vendors.length > 0 && (
          <SelectField
            label="Profile"
            value={selected || ''}
            onChange={e => {
              setSelected(e.target.value);
              setCreating(false);
            }}
          >
            {vendors.map(v => (
              <option key={v.vendorId} value={v.vendorId}>
                {v.businessName}
                {v.scope === 'org' ? ' (one organization)' : ''}
              </option>
            ))}
          </SelectField>
        )}
        {!creating && (
          <button
            type="button"
            className={cn(ui.btnSecondary, ui.btnSm)}
            onClick={() => setCreating(true)}
          >
            New vendor profile
          </button>
        )}
      </div>

      {creating || vendors.length === 0 ? (
        <ProfileForm
          isNew
          activeOrg={activeOrg}
          onSaved={v => {
            setCreating(false);
            setSelected(v.vendorId);
            load();
          }}
          onCancel={vendors.length ? () => setCreating(false) : undefined}
        />
      ) : vendor ? (
        <>
          <div className={cn(ui.row, 'mb-4')}>
            <VendorLogo vendor={{ ...vendor, logoUrl: vendor.displayLogoUrl ?? vendor.logoUrl }} />
            <div>
              <strong className="text-title-s">{vendor.businessName}</strong>
              <p className={cn(ui.hint, 'm-0')}>
                {vendor.scope === 'platform' ? 'Platform profile' : 'Organization profile'}
                {vendor.status === 'suspended' ? ' - suspended by the platform' : ''}
              </p>
            </div>
            <Link
              className={cn(ui.btnGhost, ui.btnSm)}
              to={`/marketplace/vendors/${encodeURIComponent(vendor.vendorId)}`}
            >
              View page
            </Link>
          </div>
          <ListingsPanel vendor={vendor} activeOrg={activeOrg} orgs={orgs || []} onChanged={load} />
          {activeOrg &&
            (() => {
              const here = (vendor.listings || []).find(l => l.orgId === activeOrg.orgId);
              return here && ['approved', 'suspended', 'removed'].includes(here.status) ? (
                <VendorPipeline vendor={vendor} listing={here} orgName={activeOrg.name} />
              ) : null;
            })()}
          <VendorBilling vendor={vendor} />
          <VendorLogoUpload vendor={vendor} onChanged={load} />
          <ProfileForm
            key={`p-${vendor.vendorId}-${vendor.updatedAt}`}
            vendor={vendor}
            activeOrg={activeOrg}
            onSaved={load}
          />
          <ServicesEditor
            key={`s-${vendor.vendorId}-${vendor.updatedAt}`}
            vendor={vendor}
            onSaved={load}
          />
        </>
      ) : (
        <EmptyState message="Choose a profile." />
      )}
    </>
  );
}
